import { useState } from 'react';

import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import CopyButton from '@/components/atoms/CopyButton/CopyButton';
import { cn } from '@/lib/utils';
import { InsightRawValue, InsightType, InsightValues } from '@/shared/types/analytics';
import { isKeyEnter } from '@/util/util';

type Props = { name: string; raw: InsightValues };

const formatRawValue = (raw: InsightRawValue): string => {
  if (raw === null || raw === undefined) {
    return '';
  }
  return typeof raw === 'object' ? JSON.stringify(raw) : String(raw);
};

/** Insights arrive either wrapped with reasoning metadata or as a bare value. */
const normalizeInsight = (raw: InsightValues) =>
  raw !== null && typeof raw === 'object'
    ? { value: formatRawValue(raw.value), reasoning: raw.reasoning, type: raw.type }
    : { value: formatRawValue(raw), reasoning: null, type: undefined };

const EMPTY_VALUE_PLACEHOLDER = '-';

const COPYABLE_TYPES: InsightType[] = ['email', 'phone', 'string', 'integer'];

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+$/;
const NUMERIC_SHAPE = /^[+(]?[\d\s()+-]+$/;
/** Below this, a number is a count or a score rather than an identifier. */
const MIN_IDENTIFIER_DIGITS = 7;

/** A legacy insight carries no type, so fall back to the shape of the value. */
const looksCopyable = (value: string) => {
  if (EMAIL_SHAPE.test(value)) {
    return true;
  }
  return NUMERIC_SHAPE.test(value) && value.replace(/\D/g, '').length >= MIN_IDENTIFIER_DIGITS;
};

const isCopyable = (value: string, type?: InsightType) => {
  if (!value) {
    return false;
  }
  return type ? COPYABLE_TYPES.includes(type) : looksCopyable(value);
};

/** One insight row: name over value, expandable to reveal the model's reasoning. */
export const InsightItem = ({ name, raw }: Props) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { value, reasoning, type } = normalizeInsight(raw);

  const toggle = (event: React.MouseEvent | React.KeyboardEvent) => {
    if ((event.target as HTMLElement).closest('button')) {
      return;
    }
    setOpen((prev) => !prev);
  };

  const interactive = reasoning
    ? {
        onClick: toggle,
        onKeyDown: (event: React.KeyboardEvent) => {
          if (isKeyEnter(event)) {
            toggle(event);
          }
        },
        role: 'button',
        tabIndex: 0,
        'aria-expanded': open,
        'aria-label': open ? t('common.collapse') : t('common.expand'),
      }
    : {};

  return (
    <div
      className={cn(
        'group/insight flex flex-col items-start gap-4 self-stretch rounded-lg p-3 hover:bg-selected-soft',
        reasoning && 'cursor-pointer'
      )}
      {...interactive}
    >
      <div className="flex items-center gap-4 self-stretch">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="self-stretch text-xs leading-4 font-medium text-muted-foreground">
            {name}
          </span>
          <div className="flex items-center gap-1.5 self-stretch">
            <p
              className={cn(
                'min-w-0 text-sm leading-5 font-normal wrap-anywhere',
                value ? 'text-foreground' : 'text-muted-foreground',
                !!reasoning && !open && 'line-clamp-1'
              )}
            >
              {value || EMPTY_VALUE_PLACEHOLDER}
            </p>
            {isCopyable(value, type) && (
              <CopyButton
                data={value}
                buttonSize="flush"
                className="flex size-4 shrink-0 items-center hover:[&_svg]:stroke-foreground hover:[&_svg]:text-foreground"
              />
            )}
          </div>
        </div>
        {reasoning && (
          <div className="flex shrink-0 items-start self-stretch py-3">
            <ChevronDownIcon
              size={16}
              className={cn(
                'text-muted-foreground opacity-0 transition group-hover/insight:opacity-100 group-focus-visible/insight:opacity-100',
                open && 'rotate-180 opacity-100'
              )}
            />
          </div>
        )}
      </div>

      {open && reasoning && (
        <div className="flex flex-col gap-1 self-stretch border-t border-border pt-3">
          <span className="text-xs leading-4 font-medium text-muted-foreground">
            {t('try_it.review.reasoning')}
          </span>
          <p className="text-sm leading-5 font-normal text-foreground">{reasoning}</p>
        </div>
      )}
    </div>
  );
};
